/**
 * @file useMlrActivePage.ts
 * @brief Scroll-spy for the MLR Pages outline.
 *
 * @details
 * The report scrolls inside the shell `main[data-scroll-root]`, not the
 * window. Active page = last A4 sheet whose top has crossed the *visible*
 * bottom of `#mlr-editor-chrome` (transforms count; offsetHeight does not).
 * Spy starts when the paper stack mounts so Cover/§1 exist in the DOM
 * (08-09-2026).
 *
 * @references
 * - WEBPANEL/src/shared/scroll/scrollMainTo.ts
 * - WEBPANEL/src/features/medical-assessment/components/MlrEditorChromePortal.tsx
 * - WEBPANEL/src/features/medical-assessment/components/MlrReportDocument.tsx
 *
 * @author DES
 * @date 08-09-2026
 */

import { useEffect, useState } from "react";
import {
  getMainAlignLineY,
  getMainScrollRoot,
} from "@/shared/scroll/scrollMainTo";
import { MLR_EDITOR_CHROME_ID } from "./components/MlrEditorChromePortal";

export type MlrNavPage = "cover" | "contents" | number;

/**
 * The sheet's DOM anchor. Lives beside its reader (`mlrPageFromAnchorId`) so
 * the id format is written once. (13-09-2026)
 */
export function mlrSectionAnchor(number: number): string {
  return `mlr-section-${number}`;
}

/**
 * The scroll spy only needs the ORDER of the sheets, not their titles — the
 * MLR is thirteen numbered sections by definition, so this no longer imports
 * a section list the panel would have to keep in step with the model.
 */
const MLR_SECTION_COUNT = 13;

const PAGE_IDS = [
  "mlr-cover",
  "mlr-contents",
  ...Array.from({ length: MLR_SECTION_COUNT }, (_, index) =>
    mlrSectionAnchor(index + 1),
  ),
] as const;

export function mlrPageFromAnchorId(id: string): MlrNavPage {
  if (id === "mlr-cover") return "cover";
  if (id === "mlr-contents") return "contents";
  const parsed = Number(id.replace("mlr-section-", ""));
  return Number.isFinite(parsed) ? parsed : "cover";
}

function pageAtReadingLine(stack: HTMLElement): MlrNavPage {
  const line = getMainAlignLineY(MLR_EDITOR_CHROME_ID);
  let current: MlrNavPage = "cover";
  for (const id of PAGE_IDS) {
    const node = stack.querySelector<HTMLElement>(`#${id}`);
    if (!node) continue;
    if (node.getBoundingClientRect().top <= line) {
      current = mlrPageFromAnchorId(id);
    }
  }
  return current;
}

export function useMlrActivePage(stack: HTMLElement | null): MlrNavPage {
  const [active, setActive] = useState<MlrNavPage>("cover");

  useEffect(() => {
    const root = getMainScrollRoot();
    if (!root || !stack) return;

    let frame = 0;
    const update = () => {
      frame = 0;
      const next = pageAtReadingLine(stack);
      setActive((prev) => (prev === next ? prev : next));
    };
    const onScroll = () => {
      if (frame) return;
      frame = window.requestAnimationFrame(update);
    };

    update();
    root.addEventListener("scroll", onScroll, { passive: true });
    window.addEventListener("resize", onScroll);
    const resize = new ResizeObserver(onScroll);
    resize.observe(stack);
    const chrome = document.getElementById(MLR_EDITOR_CHROME_ID);
    if (chrome) resize.observe(chrome);
    return () => {
      if (frame) window.cancelAnimationFrame(frame);
      root.removeEventListener("scroll", onScroll);
      window.removeEventListener("resize", onScroll);
      resize.disconnect();
    };
  }, [stack]);

  return active;
}
